import { useRef, useState, Fragment } from 'react'
import { Printer, Split, X as XIcon } from 'lucide-react'
import api from '../../api/client'
import AlterarComissaoModal from '../../components/modals/AlterarComissaoModal'
import IsentarGTAModal from '../../components/modals/IsentarGTAModal'
import SepararFaturaModal from '../../components/modals/SepararFaturaModal'
import { openPdf, downloadPdf } from '../../utils/pdfUtils'
import PrazoComissao from '../../components/mapas/PrazoComissao'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

const num  = v => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try { return new Date(s + (s.includes('T') ? '' : 'T12:00:00')).toLocaleDateString('pt-BR') } catch { return s }
}

// ─── Estilos de célula ────────────────────────────────────────────────────────
const CRITERIOS_COMPRADOR = [
  { id: 'tipo',     label: 'Tipo de animal' },
  { id: 'sexo',     label: 'Sexo' },
  { id: 'raca',     label: 'Raça' },
  { id: 'vendedor', label: 'Vendedor' },
]

const th  = { border: '1px solid #ccc', padding: '6px 8px', background: '#d8d8d8', fontWeight: 700, textAlign: 'center', fontSize: 13, whiteSpace: 'nowrap' }
const td  = { border: '1px solid #ccc', padding: '5px 8px', verticalAlign: 'top', fontSize: 13 }
const tdC = { ...td, textAlign: 'center' }
const tdR = { ...td, textAlign: 'right' }

// Estilos sem linhas verticais — usados só na tabela de lotes
const thL  = { borderBottom: '1px solid #ccc', borderTop: '1px solid #ccc', padding: '6px 8px', background: '#d8d8d8', fontWeight: 700, textAlign: 'center', fontSize: 13, whiteSpace: 'nowrap' }
const tdL  = { borderBottom: '1px solid #ccc', padding: '5px 8px', verticalAlign: 'top', fontSize: 13 }
const tdLC = { ...tdL, textAlign: 'center' }
const tdLR = { ...tdL, textAlign: 'right' }
const blueHdr = {
  background: '#f0f0f0', color: '#000', fontWeight: 700, fontSize: 14,
  padding: '6px 10px', textAlign: 'center', marginBottom: 0,
  border: '1px solid #000',
}

// ─── Uma página de comprador ──────────────────────────────────────────────────
function FaturaPage({ comp, leilao, organizador, showDbVendedor, showMediasSexo, showMediaGeral, showGtaNota, ocultarLinhaGtaNota, ocultarColunaGado, comissaoEmLinha, ocultarParcelas, colunasAtivas, ordenarPgtosPorData, totalCompraModo }) {
  const lotes = (comp.lotes || []).filter(l => l.situacao === 'VENDA')
  // Defesas (tipo "Vendas e Defesas"): seção própria; a comissão de defesa soma no total
  const lotesDef = (comp.lotes || []).filter(l => l.situacao === 'DEFESA')
  const totalDefVv = lotesDef.reduce((s, l) => s + (l.vv || 0), 0)
  const totalDefCv = lotesDef.reduce((s, l) => s + (l.cv_val || 0), 0)

  const fluxoLeilao = leilao.fluxo_comissao || ''
  const isSeparada = fluxoLeilao === 'separada' || fluxoLeilao === 'cavalo'
  const isCavalo = fluxoLeilao === 'cavalo'

  // Cálculos por lote
  const lotesCalc = lotes.map(l => ({
    ...l,
    vlr_liquido: isSeparada
      ? (l.vv || 0) + (l.cc_val || 0)
      : (l.vv || 0) - (l.cv_val || 0) - (l.desconto || 0),
    prazo_data:  l.parcelas?.[0]?.data ?? null,
  }))

  // Totais gerais
  const totalVv   = lotes.reduce((s, l) => s + (l.vv  || 0), 0)
  // Fluxo cavalo: comprador paga a 1ª parcela agora, resto agendado depois.
  const totalPrimeiraParcela = lotes.reduce((s, l) => s + (l.parcelas?.[0]?.valor || 0), 0)
  const totalCc   = lotes.reduce((s, l) => s + (l.cc_val || 0), 0)
  const totalCv   = lotes.reduce((s, l) => s + (l.cv_val || 0), 0)
  const totalDesp = lotes.reduce((s, l) => s + (l.desp || 0), 0)
  const totalDesconto = lotes.reduce((s, l) => s + (l.desconto || 0), 0)
  const totalGeral = isSeparada
    ? totalVv + totalCc
    : totalVv - totalCv - totalDesconto

  // Cálculo GTA + Nota — isenção POR LOTE e INDEPENDENTE por componente
  // (gta_isento / nota_isento), igual ao backend (reports.base.calcular_gta_nota).
  const gtaIsento = !!comp.gta_isento && !!comp.nota_isento  // tudo isento → esconde bloco
  let totalGtaNota = 0
  if (fluxoLeilao === 'gta_nota' && !gtaIsento) {
    const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
    const valorNota = parseFloat(leilao.valor_nota) || 0
    const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
    const lotesGta = lotes.filter(l => !l.gta_isento)
    const lotesNota = lotes.filter(l => !l.nota_isento)

    if (valorPorAnimal > 0 || valorNota > 0) {
      const gtaTotal = valorPorAnimal * lotesGta.reduce((s, l) => s + (l.qtd || 0), 0)
      let notaTotal = 0
      if (gtaModo === 'por_lote') {
        notaTotal = valorNota * lotesNota.length
      } else {
        const vendedoresSet = new Set()
        lotesNota.forEach(l => { if (l.vendedor_id) vendedoresSet.add(l.vendedor_id) })
        notaTotal = valorNota * vendedoresSet.size
      }
      totalGtaNota = gtaTotal + notaTotal
    }
  }

  // PAGAMENTOS VENDEDORES — agrupa por vendedor + data de vencimento
  const normalizeDate = (d) => {
    if (!d) return ''
    const s = String(d)
    if (s.includes('T')) return s.split('T')[0]
    return s.slice(0, 10)
  }
  const vendedoresMap = new Map()
  lotes.forEach(l => {
    const vKey = l.vendedor_nome || ''
    if (!vendedoresMap.has(vKey)) {
      const dbs = l.vendedor_dados_bancarios || []
      vendedoresMap.set(vKey, {
        vendedor: vKey,
        db: dbs.find(d => d.is_principal) || dbs[0] || null,
        parcelas: new Map(),
        total: 0,
      })
    }
    const vEntry = vendedoresMap.get(vKey)
    ;(l.parcelas || []).forEach(p => {
      const normDate = normalizeDate(p.data)
      const pKey = `${normDate}`
      if (!vEntry.parcelas.has(pKey)) {
        vEntry.parcelas.set(pKey, { data: normDate, prazo: p.prazo || '', valor: 0, lotesNums: [] })
      }
      const pEntry = vEntry.parcelas.get(pKey)
      pEntry.valor += p.liquido || 0
      vEntry.total += p.liquido || 0
      if (!pEntry.lotesNums.includes(l.numero_lote)) pEntry.lotesNums.push(l.numero_lote)
    })
  })
  let pagamentosVendedores = Array.from(vendedoresMap.values()).map(v => ({
    ...v,
    parcelas: Array.from(v.parcelas.values()).sort((a, b) => (a.data || '').localeCompare(b.data || '')),
  }))

  // Ordenar por data de vencimento se habilitado
  if (ordenarPgtosPorData) {
    pagamentosVendedores = pagamentosVendedores.sort((a, b) => {
      const dataA = a.parcelas[0]?.data || '9999';
      const dataB = b.parcelas[0]?.data || '9999';
      return dataA.localeCompare(dataB);
    });
  }

  // MÉDIAS POR SEXO
  const sexoMap = new Map()
  lotes.forEach(l => {
    const sexo = (l.sexo || 'N/A').toUpperCase()
    if (!sexoMap.has(sexo)) sexoMap.set(sexo, { sexo, qtd_lotes: 0, qtd_animais: 0, peso_total: 0, valor_total: 0 })
    const e = sexoMap.get(sexo)
    e.qtd_lotes++
    e.qtd_animais  += l.qtd  || 0
    e.peso_total   += (l.peso || 0) * (l.qtd || 0)
    e.valor_total  += (l.vv || 0) + (l.cc_val || 0)
  })
  // Rateia GTA+Nota proporcionalmente por sexo
  const totalAnimaisSexo = Array.from(sexoMap.values()).reduce((s, v) => s + v.qtd_animais, 0)
  if (totalGtaNota > 0 && totalAnimaisSexo > 0) {
    sexoMap.forEach(s => {
      s.valor_total += totalGtaNota * (s.qtd_animais / totalAnimaisSexo)
    })
  }
  const mediasSexo = Array.from(sexoMap.values()).map(s => ({
    ...s,
    peso_medio:   s.qtd_animais > 0 ? s.peso_total / s.qtd_animais : 0,
    preco_kg:     s.peso_total  > 0 ? s.valor_total / s.peso_total  : 0,
    media_animal: s.qtd_animais > 0 ? s.valor_total / s.qtd_animais : 0,
    media_arroba: s.peso_total  > 0 ? s.valor_total / (s.peso_total / 30) : 0,
  }))

  // CONTAGEM POR SEXO
  const qtdFemea = lotes.reduce((s, l) => {
    const sx = (l.sexo || '').toUpperCase()
    return s + (sx === 'FEMEA' || sx === 'FÊMEA' ? (l.qtd || 0) : 0)
  }, 0)
  const qtdMacho = lotes.reduce((s, l) => {
    return s + ((l.sexo || '').toUpperCase() === 'MACHO' ? (l.qtd || 0) : 0)
  }, 0)

  // MÉDIA GERAL
  const totalAnimais   = lotes.reduce((s, l) => s + (l.qtd || 0), 0)
  const mediaAnimal    = totalAnimais > 0 ? (totalVv + totalCc + totalGtaNota) / totalAnimais : 0
  const totalVendedores = new Set(lotes.map(l => l.vendedor_nome)).size

  // GTA/Nota (dados virão do backend futuro — por ora zerado)
  const totalCrias = lotes.reduce(
    (s, l) => s + (l.qtd_crias_femeas || 0) + (l.qtd_crias_machos || 0), 0
  )

  const orgDb  = organizador.dados_bancarios
  const logoSrc = organizador.logo_url
    ? (organizador.logo_url.startsWith('http')
        ? organizador.logo_url
        : `${API_BASE}${organizador.logo_url.startsWith('/') ? '' : '/'}${organizador.logo_url}`)
    : null

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 28px' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 6 }}>
        {/* Logo do organizador (esquerda) */}
        <div style={{ width: 200 }}>
          {logoSrc
            ? <img src={logoSrc} alt="logo" style={{ maxHeight: 90, maxWidth: 200, objectFit: 'contain' }} />
            : <span style={{ fontWeight: 700, fontSize: 14 }}>{organizador.nome}</span>}
        </div>
        {/* Título central */}
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a3c6b' }}>Fatura de Comprador</div>
          <div style={{ fontSize: 13, marginTop: 3 }}>{leilao.nome} - {fmtDate(leilao.data_leilao)}</div>
          <div style={{ fontSize: 13 }}>{organizador.nome}</div>
          {comp.titulo_fatura && <div style={{ fontSize: 14, fontWeight: 700, color: '#1a3c6b', marginTop: 3 }}>{comp.titulo_fatura}</div>}
        </div>
        {/* Logo Rematei (direita) */}
        <div style={{ width: 150, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <img src="/assets/rematei.png" alt="Rematei" style={{ height: 64, objectFit: 'contain' }} />
        </div>
      </div>

      {/* ── Nome do comprador (banner) ── */}
      <div style={{ background: '#f0f0f0', color: '#000', fontWeight: 700, fontSize: 17, padding: '4px 0', textAlign: 'center', marginBottom: 10, border: '1px solid #000', lineHeight: 1.2 }}>
        {comp.nome.toUpperCase()}
      </div>

      {/* ── Dados do comprador ── */}
      {(() => {
        const tdComp = { border: '1px solid #ccc', padding: '3px 6px', fontSize: 11, lineHeight: 1.4, verticalAlign: 'top' }
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
            <tbody>
              <tr>
                <td style={{ ...tdComp, width: '33%' }}><strong>CPF/CNPJ:</strong> {comp.cpf_cnpj || '–'}</td>
                <td style={{ ...tdComp, width: '33%' }}><strong>CEP:</strong> {comp.cep || '–'}</td>
                <td style={{ ...tdComp, width: '34%' }}><strong>Fazenda:</strong> {comp.fazenda || '–'}</td>
              </tr>
              <tr>
                <td style={tdComp}><strong>Endereço:</strong> {[comp.endereco, comp.numero, comp.bairro].filter(Boolean).join(', ') || '–'}</td>
                <td style={tdComp}><strong>Telefone:</strong> {comp.telefone || '–'}</td>
                <td style={tdComp}><strong>I.E.:</strong> {comp.fazenda_ie || '–'}</td>
              </tr>
              <tr>
                <td style={tdComp}><strong>Município:</strong> {[comp.cidade, comp.uf].filter(Boolean).join(' - ') || '–'}</td>
                <td style={tdComp}><strong>Email:</strong> {comp.email || '–'}</td>
                <td style={tdComp}><strong>Cidade/UF:</strong> {[comp.fazenda_cidade, comp.fazenda_uf].filter(Boolean).join(' - ') || '–'}</td>
              </tr>
            </tbody>
          </table>
        )
      })()}

      {/* ── Tabela de lotes ── */}
      {(() => {
        const cols = colunasAtivas || ['lote', 'vendedor', 'qtd', 'raca', 'sexo', 'peso', 'valor_unitario', 'valor_total', 'comissao_comprador', 'total_liquido', 'prazo']
        const activeCols = cols.filter(c => {
          const valid = ['lote','vendedor','qtd','raca','animal','sexo','tipo','peso','valor_unitario','valor_total','comissao_comprador','total_liquido','prazo']
          return valid.includes(c)
        })
        const temAnimalCol = activeCols.includes('animal')
        const linhasMultilinha = (texto) => (texto || '–').split('<br/>').map((linha, idx) => <div key={idx}>{linha}</div>)
        const colDefs = {
          lote:               { label: 'Lote',              render: l => <td key="lote" style={{ ...tdLC, fontWeight: 700 }}>{l.numero_lote}</td> },
          vendedor:           { label: 'Vendedor',          render: l => <td key="vendedor" style={{ ...tdL, whiteSpace: 'nowrap' }}>{l.vendedor_exibicao || l.vendedor_nome || ''}</td> },
          qtd:                { label: 'Qtd',               render: l => <td key="qtd" style={tdLC}>{l.qtd}</td> },
          raca:               { label: 'Raça',              render: l => (
            <td key="raca" style={tdLC}>{temAnimalCol ? linhasMultilinha(l.raca_animais) : (l.raca_resumo || l.raca || '–')}</td>
          ) },
          animal:             { label: 'Animal',            render: l => (
            <td key="animal" style={{ ...tdL, whiteSpace: 'nowrap' }}>
              {linhasMultilinha(l.animal_nome)}
            </td>
          ) },
          sexo:               { label: 'Sx',                render: l => (
            <td key="sexo" style={tdLC}>{temAnimalCol && l.sexo_animais ? linhasMultilinha(l.sexo_animais) : (l.sexo ? l.sexo.charAt(0).toUpperCase() : '–')}</td>
          ) },
          tipo:               { label: 'Tipo',              render: l => <td key="tipo" style={tdLC}>{l.tipo_animal || '–'}</td> },
          peso:               { label: 'Peso',              render: l => <td key="peso" style={tdLR}>{num(l.peso)}</td> },
          valor_unitario:     { label: 'Valor Unit.',       render: l => <td key="vunit" style={tdLR}>{num(l.vv_unit)}</td> },
          valor_total:        { label: 'Vlr Total Lote',    render: l => <td key="vtotal" style={tdLR}>{num(l.vv)}</td> },
          comissao_comprador: { label: 'Valor Com. Comp.',  render: l => <td key="cc" style={{ ...tdLC, fontWeight: 700 }}>{num(l.cc_pct)}% - {num(l.cc_val)}</td> },
          total_liquido:      { label: isSeparada ? 'Total + Com. Comp.' : 'Total - Com. Ven.', render: l => <td key="tliq" style={{ ...tdLR, fontWeight: 700 }}>{num(l.vlr_liquido)}</td> },
          prazo:              { label: 'Prazo',             render: l => <td key="prazo" style={tdLC}>{fmtDate(l.prazo_data)}</td> },
        }

        // Footer totals mapping
        const footerMap = {
          valor_total: <td key="ft_vt" style={{ ...tdLR, fontSize: 13, whiteSpace: 'nowrap', fontWeight: 700 }}>R$ {num(totalVv)}</td>,
          comissao_comprador: <td key="ft_cc" style={{ ...tdLC, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>R$ {num(totalCc)}</td>,
          total_liquido: <td key="ft_tl" style={{ ...tdLR, fontSize: 13, whiteSpace: 'nowrap', fontWeight: 700 }}>R$ {num(totalGeral)}</td>,
        }

        // Determine span for the first part of footer (fêmeas/machos + Totais)
        const firstTotalIdx = activeCols.findIndex(c => c === 'valor_total' || c === 'comissao_comprador' || c === 'total_liquido')
        const spanCount = firstTotalIdx > 0 ? firstTotalIdx : activeCols.length

        return (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 11 }}>
            <thead>
              <tr>
                {activeCols.map(c => <th key={c} style={thL}>{colDefs[c].label}</th>)}
              </tr>
            </thead>
            <tbody>
              {lotesCalc.map((l, i) => {
                const criasParts = []
                if (l.qtd_crias_machos) criasParts.push(`${l.qtd_crias_machos} macho(s)`)
                if (l.qtd_crias_femeas) criasParts.push(`${l.qtd_crias_femeas} fêmea(s)`)
                const bg = i % 2 === 0 ? '#fff' : '#f5f5f5'
                return (
                  <Fragment key={i}>
                    <tr style={{ background: bg }}>
                      {activeCols.map(c => colDefs[c].render(l))}
                    </tr>
                    {criasParts.length > 0 && (
                      <tr style={{ background: '#fafafa' }}>
                        <td colSpan={activeCols.length} style={{ ...tdL, fontSize: 10, padding: '1px 6px' }}>
                          <strong>Crias:</strong> {criasParts.join(', ')}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f0f0f0', fontWeight: 700, borderTop: '2px solid #000' }}>
                <td colSpan={spanCount} style={{ ...tdL, fontWeight: 700, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 400 }}>
                      <strong>{totalCrias > 0 ? 'Fêmeas:' : 'Total de Fêmeas:'}</strong> <span style={{ fontSize: 14 }}>{qtdFemea}</span>
                      &nbsp;&nbsp;&nbsp;
                      <strong>{totalCrias > 0 ? 'Machos:' : 'Total de Machos:'}</strong> <span style={{ fontSize: 14 }}>{qtdMacho}</span>
                      {totalCrias > 0 && (
                        <>
                          &nbsp;&nbsp;&nbsp;
                          <strong>Crias:</strong> <span style={{ fontSize: 14 }}>{lotes.reduce((s, l) => s + (l.qtd_crias_machos || 0), 0)}M / {lotes.reduce((s, l) => s + (l.qtd_crias_femeas || 0), 0)}F</span>
                        </>
                      )}
                    </span>
                    <span>Totais:</span>
                  </div>
                </td>
                {activeCols.slice(spanCount).map(c => footerMap[c] || <td key={`ft_${c}`} style={tdL}></td>)}
              </tr>
            </tfoot>
          </table>
        )
      })()}

      {/* ── DEFESAS ── */}
      {lotesDef.length > 0 && (
        <>
          <div style={blueHdr}>DEFESAS</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 12 }}>
            <thead>
              <tr>
                {['Lote', 'Qtd', 'Raça', 'Sx', 'Vlr Unit.', 'Vlr Total Lote', 'Com. Defesa'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lotesDef.map((l, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                  <td style={tdLC}><strong>{l.numero_lote}</strong></td>
                  <td style={tdLC}>{l.qtd}</td>
                  <td style={tdLC}>{l.raca_resumo || l.raca || '–'}</td>
                  <td style={tdLC}>{(l.sexo || '').slice(0, 1).toUpperCase() || '–'}</td>
                  <td style={tdLR}>{num(l.vv_unit || 0)}</td>
                  <td style={tdLR}>{num(l.vv || 0)}</td>
                  <td style={{ ...tdLC, fontWeight: 700 }}>{num(l.cv_pct || 0)}% - {num(l.cv_val || 0)}</td>
                </tr>
              ))}
              <tr style={{ background: '#f0f0f0', fontWeight: 700, borderTop: '2px solid #000' }}>
                <td colSpan={5} style={{ ...tdL, fontWeight: 700 }}>
                  Total: {lotesDef.reduce((s, l) => s + (l.qtd || 0), 0)} animais
                </td>
                <td style={{ ...tdLR, fontWeight: 700 }}>{num(totalDefVv)}</td>
                <td style={{ ...tdLC, fontWeight: 700 }}>{num(totalDefCv)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* ── PAGAMENTOS VENDEDORES ── */}
      <div style={blueHdr}>{comissaoEmLinha ? 'LISTA DE PAGAMENTOS' : 'PAGAMENTOS VENDEDORES'}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 12 }}>
        <thead>
          <tr>
            {[comissaoEmLinha ? 'Favorecido' : 'Vendedor','Valor','Data','Lote(s)','Condição'].map(h => (
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Linha de comissão como primeira linha quando comissaoEmLinha ativo */}
          {comissaoEmLinha && (() => {
            const fluxo = comp.fluxo_comissao || leilao.fluxo_comissao || 'primeira_parcela'
            const isSep = fluxo === 'separada' || fluxo === 'cavalo'
            const isGtaNota = fluxo === 'gta_nota'
            const totalCvInline = lotes.reduce((s, l) => s + (l.cv_val || 0), 0)
            const totalComInline = isSep ? totalCc : totalCc + totalCvInline
            const totalComTaxasInline = totalComInline + totalGtaNota + (isSep ? 0 : totalDesp)
            const orgDb = organizador.dados_bancarios?.[0] || organizador.dados_bancarios || null

            // Detalhamento para a coluna mesclada
            const detalhes = []
            detalhes.push(`Comissão: R$ ${num(totalComInline)}`)
            if (!isSep && totalDesp > 0) detalhes.push(`Despesas: R$ ${num(totalDesp)}`)
            if (isGtaNota && totalGtaNota > 0) detalhes.push(`GTA/Nota: R$ ${num(totalGtaNota)}`)

            return (
              <tr style={{ background: '#ebebeb' }}>
                <td style={{ ...td, fontWeight: 700, verticalAlign: 'middle', textAlign: 'center', fontSize: 12 }}>
                  {(organizador.nome || '').toUpperCase()}
                  {organizador.cnpj && <div style={{ fontWeight: 400, fontSize: 11 }}>CNPJ: {organizador.cnpj}</div>}
                  {showDbVendedor && orgDb && (
                    <div style={{ fontWeight: 400, fontSize: 10, color: '#333', marginTop: 3, lineHeight: 1.4 }}>
                      {orgDb.banco && <div>BANCO: {orgDb.banco.toUpperCase()}</div>}
                      {orgDb.agencia && <div>AG: {orgDb.agencia} C/C: {orgDb.conta}</div>}
                      {orgDb.pix && <div style={{ marginTop: 2 }}><strong style={{ fontSize: 13 }}>PIX:</strong> <strong style={{ fontSize: 12 }}>{orgDb.pix.toUpperCase()}</strong></div>}
                    </div>
                  )}
                </td>
                <td style={{ ...tdC, fontWeight: 700, fontSize: 13, verticalAlign: 'middle' }}>R$ {num(totalComTaxasInline)}</td>
                <td style={{ ...tdC, verticalAlign: 'middle', fontWeight: 700, fontSize: 12 }}>{fmtDate(leilao.data_leilao)}</td>
                <td colSpan={2} style={{ ...tdC, fontSize: 12, lineHeight: 1.5, textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                  {detalhes.join(' | ')}
                </td>
              </tr>
            )
          })()}
          {pagamentosVendedores.map((v, vi) => {
            const nParc = v.parcelas.length;
            const GRID_THRESHOLD = 6;
            const bg = vi % 2 === 0 ? '#fff' : '#f5f5f5';
            const borderTop = vi > 0 ? '2px solid #ccc' : '1px solid #e0e0e0';

            if (nParc > GRID_THRESHOLD) {
              // Modo grid compacto
              const totalVend = v.parcelas.reduce((s, p) => s + (p.valor || 0), 0);
              const allLotes = [...new Set(v.parcelas.flatMap(p => p.lotesNums))];
              return (
                <Fragment key={`v-${vi}`}>
                  <tr style={{ background: bg }}>
                    <td style={{ ...td, fontWeight: 700, verticalAlign: 'middle', textAlign: comissaoEmLinha ? 'center' : 'left', borderTop, background: bg }}>
                      {(v.vendedor || '').toUpperCase()}
                      {!ocultarParcelas && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: '#555', marginTop: 2 }}>
                          Parcelas: {nParc}
                        </div>
                      )}
                      {showDbVendedor && v.db && (
                        <div style={{ fontWeight: 400, fontSize: 10, color: '#333', marginTop: 3, lineHeight: 1.4 }}>
                          {v.db.banco && <div>BANCO: {v.db.banco.toUpperCase()}</div>}
                          {v.db.agencia && <div>AG: {v.db.agencia}  C/C: {v.db.conta}</div>}
                          {v.db.is_terceiro && v.db.titular && <div>TITULAR: {v.db.titular.toUpperCase()}</div>}
                          {v.db.cpf_cnpj_titular && <div>CPF/CNPJ: {v.db.cpf_cnpj_titular}</div>}
                          {v.db.observacoes && <div style={{ color: '#666', fontStyle: 'italic' }}>OBS: {v.db.observacoes}</div>}
                          {v.db.pix && <div style={{ marginTop: 3 }}><strong style={{ fontSize: 13 }}>PIX:</strong> <strong style={{ fontSize: 12 }}>{v.db.pix.toUpperCase()}</strong></div>}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdC, fontWeight: 700, borderTop }}>R$ {num(totalVend)}</td>
                    <td style={{ ...tdC, borderTop }}>{nParc} parcelas</td>
                    <td style={{ ...tdC, borderTop }}>{allLotes.join(', ')}</td>
                    <td style={{ ...tdC, borderTop }}>Parcelado</td>
                  </tr>
                  <tr style={{ background: bg }}>
                    <td colSpan={5} style={{ padding: '4px 6px', borderTop: '1px solid #e0e0e0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                        <tbody>
                          {(() => {
                            const ncols = 4;
                            const rows = [];
                            for (let r = 0; r < Math.ceil(nParc / ncols); r++) {
                              const cells = [];
                              for (let c = 0; c < ncols; c++) {
                                const idx = r * ncols + c;
                                if (idx < nParc) {
                                  const p = v.parcelas[idx];
                                  cells.push(
                                    <td key={c} style={{ border: '1px solid #ddd', padding: '2px 4px', fontSize: 10 }}>
                                      {String(idx + 1).padStart(2, '0')}/{nParc} - {fmtDate(p.data)} - R$ {num(p.valor)}
                                    </td>
                                  );
                                } else {
                                  cells.push(<td key={c} style={{ border: '1px solid #ddd', padding: '2px 4px' }}></td>);
                                }
                              }
                              rows.push(<tr key={r}>{cells}</tr>);
                            }
                            return rows;
                          })()}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </Fragment>
              );
            }

            // Modo normal: uma linha por parcela
            return v.parcelas.map((p, pi) => {
              const borderTopNormal = pi === 0 && vi > 0 ? '2px solid #ccc' : '1px solid #e0e0e0';
              return (
                <tr key={`v-${vi}-p-${pi}`} style={{ background: bg }}>
                  {pi === 0 && (
                    <td rowSpan={nParc} style={{ ...td, fontWeight: 700, verticalAlign: 'middle', textAlign: comissaoEmLinha ? 'center' : 'left', borderTop: borderTopNormal, background: bg }}>
                      {(v.vendedor || '').toUpperCase()}
                      {!ocultarParcelas && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: '#555', marginTop: 2 }}>
                          Parcelas: {nParc}
                        </div>
                      )}
                      {showDbVendedor && v.db && (
                        <div style={{ fontWeight: 400, fontSize: 10, color: '#333', marginTop: 3, lineHeight: 1.4 }}>
                          {v.db.banco   && <div>BANCO: {v.db.banco.toUpperCase()}</div>}
                          {v.db.agencia && <div>AG: {v.db.agencia}  C/C: {v.db.conta}</div>}
                          {v.db.is_terceiro && v.db.titular && <div>TITULAR: {v.db.titular.toUpperCase()}</div>}
                          {v.db.cpf_cnpj_titular && <div>CPF/CNPJ: {v.db.cpf_cnpj_titular}</div>}
                          {v.db.observacoes && <div style={{ color: '#666', fontStyle: 'italic' }}>OBS: {v.db.observacoes}</div>}
                          {v.db.pix     && <div style={{ marginTop: 3 }}><strong style={{ fontSize: 13 }}>PIX:</strong> <strong style={{ fontSize: 12 }}>{v.db.pix.toUpperCase()}</strong></div>}
                        </div>
                      )}
                    </td>
                  )}
                  <td style={{ ...tdC, fontWeight: 700, borderTop: borderTopNormal }}>R$ {num(p.valor)}</td>
                  <td style={{ ...tdC, borderTop: borderTopNormal }}>{fmtDate(p.data)}</td>
                  <td style={{ ...tdC, borderTop: borderTopNormal }}>{p.lotesNums.join(', ')}</td>
                  <td style={{ ...tdC, borderTop: borderTopNormal }}>{p.prazo || '–'}</td>
                </tr>
              );
            });
          })}
        </tbody>
      </table>

      {/* ── CÁLCULO GTA E NOTA ── */}
      {showGtaNota && !gtaIsento && (() => {
        const valorGtaAnimal = parseFloat(leilao.valor_por_animal) || 0
        const valorNota      = parseFloat(leilao.valor_nota) || 0
        const gtaModo        = leilao.gta_modo_cobranca || 'por_vendedor'
        // Exclui lotes isentos por componente: GTA por gta_isento, Nota por nota_isento
        const lotesGta       = lotes.filter(l => !l.gta_isento)
        const lotesNota      = lotes.filter(l => !l.nota_isento)
        const qtdAnimais     = lotesGta.reduce((s, l) => s + (l.qtd || 0), 0)
        const qtdCriasF      = lotesGta.reduce((s, l) => s + (l.qtd_crias_femeas || 0), 0)
        const qtdCriasM      = lotesGta.reduce((s, l) => s + (l.qtd_crias_machos || 0), 0)
        const totalAnimCrias = qtdAnimais + qtdCriasF + qtdCriasM
        const totalGTA       = valorGtaAnimal * totalAnimCrias
        const qtdVendedores  = new Set(lotesNota.map(l => l.vendedor_id).filter(Boolean)).size
        const qtdLotes       = lotesNota.length
        const totalNota      = gtaModo === 'por_lote' ? valorNota * qtdLotes : valorNota * qtdVendedores
        const totalGtaNF     = totalGTA + totalNota
        return (
          <>
            <div style={blueHdr}>CÁLCULO GTA E NOTA</div>
            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 700 }}>
                <thead>
                  <tr>
                    {[
                      'GTA/animal','Qtd animais','Crias F','Crias M','Total c/ crias','Total GTA',
                      'Nota/vend.','Qtd vendedores','Total Nota','GTA + Nota'
                    ].map(h => (
                      <th key={h} style={{ ...th, fontSize: 9, whiteSpace: 'normal', lineHeight: 1.2, padding: '4px 4px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...tdR, fontSize: 10, padding: '4px 4px' }}>{num(valorGtaAnimal)}</td>
                    <td style={{ ...tdC, fontSize: 10, padding: '4px 4px' }}>{qtdAnimais}</td>
                    <td style={{ ...tdC, fontSize: 10, padding: '4px 4px' }}>{qtdCriasF}</td>
                    <td style={{ ...tdC, fontSize: 10, padding: '4px 4px' }}>{qtdCriasM}</td>
                    <td style={{ ...tdC, fontSize: 10, padding: '4px 4px' }}>{totalAnimCrias}</td>
                    <td style={{ ...tdR, fontSize: 10, padding: '4px 4px' }}>{num(totalGTA)}</td>
                    <td style={{ ...tdR, fontSize: 10, padding: '4px 4px' }}>{num(valorNota)}</td>
                    <td style={{ ...tdC, fontSize: 10, padding: '4px 4px' }}>{qtdVendedores}</td>
                    <td style={{ ...tdR, fontSize: 10, padding: '4px 4px' }}>{num(totalNota)}</td>
                    <td style={{ ...tdR, fontSize: 10, padding: '4px 4px', fontWeight: 700 }}>{num(totalGtaNF)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )
      })()}

      {/* ── MÉDIAS POR SEXO ── */}
      {showMediasSexo && (<>
        <div style={blueHdr}>MÉDIAS POR SEXO (TOTAL + COMISSÃO)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 11 }}>
          <thead>
            <tr>
              {['Sexo','Qtd de Lotes','Qtd Animais','Peso Médio (kg)','Preço/kg','Média Animal','R$/@','Valor Total'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mediasSexo.map((s, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                <td style={tdC}>{s.sexo}</td>
                <td style={tdC}>{s.qtd_lotes}</td>
                <td style={tdC}>{s.qtd_animais}</td>
                <td style={tdR}>{num(s.peso_medio)}</td>
                <td style={tdR}>{num(s.preco_kg)}</td>
                <td style={tdR}>{num(s.media_animal)}</td>
                <td style={tdR}>{num(s.media_arroba)}</td>
                <td style={tdR}>{num(s.valor_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>)}

      {/* ── MÉDIA GERAL ── */}
      {showMediaGeral && (<>
        <div style={blueHdr}>MÉDIA GERAL</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 11 }}>
          <thead>
            <tr>
              {['Total de Animais','Total de Lotes','Valor Total','Média Animal'].map(h => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdC}>{totalAnimais}</td>
              <td style={tdC}>{lotes.length}</td>
              <td style={tdC}>{num(totalVv + totalCc + totalGtaNota)}</td>
              <td style={tdC}>{num(mediaAnimal)}</td>
            </tr>
          </tbody>
        </table>
      </>)}

      {/* ── PAGAMENTO COMISSÃO ── */}
      {!comissaoEmLinha && (() => {
        // fluxo_comissao vem do leilão via dados; fallback 'primeira_parcela'
        const fluxo = comp.fluxo_comissao || leilao.fluxo_comissao || 'primeira_parcela'
        const isSeparada = fluxo === 'separada' || fluxo === 'cavalo'

        const totalCv = lotes.reduce((s, l) => s + (l.cv_val || 0), 0)
        const totalComissoes = isSeparada ? totalCc : totalCc + totalCv

        // Coluna "Com. + GTA/Nota": exibe cc+cv+desp+gta (total de comissões cobradas)
        const totalComTaxas = (isSeparada
          ? totalComissoes + totalGtaNota
          : totalComissoes + totalDesp + totalGtaNota) + totalDefCv

        // Coluna "Gado + Com. + GTA/Nota": só comissão de compra + gta (o que o comprador paga)
        const totalGadoComTaxas = totalCc + totalGtaNota

        // Header da coluna: muda se tem GTA/Nota
        const colComLabel = totalGtaNota > 0 ? 'Com. + GTA/Nota' : 'Com. + Taxas'

        return (
          <>
            <div style={{ border: '1px solid #000', marginBottom: 20 }}>
              <div style={{ ...blueHdr, border: 'none', marginBottom: 0 }}>PAGAMENTO COMISSÃO</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '33%' }} />
                  <col style={{ width: '37%' }} />
                  <col style={{ width: '30%' }} />
                </colgroup>
                <thead>
                  <tr>
                    {['Organizador', 'Dados Bancários', colComLabel].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={td}>
                      <div style={{ fontWeight: 700 }}>{organizador.nome}</div>
                      {organizador.cnpj && <div>CNPJ: {organizador.cnpj}</div>}
                    </td>
                    <td style={td}>
                      {orgDb ? (
                        <>
                          {orgDb.banco   && <div>Banco: {orgDb.banco}</div>}
                          {orgDb.agencia && <div>AG: {orgDb.agencia} &nbsp; C/C: {orgDb.conta}</div>}
                          {orgDb.pix     && <div style={{ marginTop: 2 }}><strong style={{ fontSize: 13 }}>PIX:</strong> <strong style={{ fontSize: 12 }}>{orgDb.pix}</strong></div>}
                          {orgDb.observacoes && <div style={{ whiteSpace: 'pre-line' }}>OBS: {orgDb.observacoes}</div>}
                        </>
                      ) : '–'}
                    </td>
                    <td style={{ ...td, verticalAlign: 'middle', textAlign: 'center', fontSize: 11, lineHeight: 1.6 }}>
                      {ocultarLinhaGtaNota && totalGtaNota > 0 ? (
                        <>
                          {totalDesconto > 0 && <div style={{ color: '#92400e' }}><strong>Desconto:</strong> -R$ {num(totalDesconto)}</div>}
                          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 2 }}><strong>Total:</strong> R$ {num(totalComTaxas)}</div>
                        </>
                      ) : (
                        <>
                          <div><strong>Comissões:</strong> R$ {num(totalComissoes)}</div>
                          {totalDefCv > 0 && <div><strong>Com. Defesa:</strong> R$ {num(totalDefCv)}</div>}
                          {!isSeparada && <div><strong>Despesas:</strong> R$ {num(totalDesp)}</div>}
                          {totalGtaNota > 0 && <div><strong>GTA + Nota:</strong> R$ {num(totalGtaNota)}</div>}
                          {totalDesconto > 0 && <div style={{ color: '#92400e' }}><strong>Desconto:</strong> -R$ {num(totalDesconto)}</div>}
                          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 2 }}><strong>Total:</strong> R$ {num(totalComTaxas)}</div>
                        </>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )
      })()}

      {/* ── VENCIMENTO DA COMISSÃO (prazo definido pelo usuário — fluxo primeira_parcela) ── */}
      {fluxoLeilao === 'primeira_parcela' && comp.comissao_prazo?.data_vencimento && (() => {
        const [a, m, d] = String(comp.comissao_prazo.data_vencimento).slice(0, 10).split('-')
        const dias = comp.comissao_prazo.prazo_dias || 0
        return (
          <div style={{ border: '1px solid #000', marginBottom: 20, padding: '7px 10px', textAlign: 'center', fontSize: 13, background: '#f0f0f0' }}>
            <strong>VENCIMENTO DA COMISSÃO:</strong> {d}/{m}/{a} ({dias <= 0 ? 'à vista, na data do leilão' : `prazo de ${dias} dia${dias === 1 ? '' : 's'}`})
          </div>
        )
      })()}

      {/* ── PARCELAMENTO DA COMISSÃO DE COMPRA (fluxo cavalo, opcional) ── */}
      {isCavalo && lotes.some(l => l.comissao_parcelamento) && (
        <div style={{ border: '1px solid #000', marginBottom: 20 }}>
          <div style={{ ...blueHdr, border: 'none', marginBottom: 0 }}>PARCELAMENTO DA COMISSÃO DE COMPRA</div>
          <div style={{ padding: 10 }}>
            {lotes.filter(l => l.comissao_parcelamento).map((l, li) => {
              const sch = l.comissao_parcelamento
              return (
                <div key={li} style={{ marginBottom: li < lotes.length - 1 ? 12 : 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 4 }}>Lote {l.numero_lote}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: 'left' }}>Descrição</th>
                        <th style={{ ...th, textAlign: 'center' }}>Vencimento</th>
                        <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sch.linhas.map((ln, i) => (
                        <tr key={i}>
                          <td style={td}>{ln.rotulo}</td>
                          <td style={{ ...td, textAlign: 'center' }}>{ln.data ? fmtDate(ln.data) : '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>R$ {num(ln.valor)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td style={{ ...td, fontWeight: 700 }}>Total</td>
                        <td style={td}></td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>R$ {num(sch.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Total Geral ── */}
      {(() => {
        const totalAPagar = (isCavalo ? totalPrimeiraParcela : totalVv) + totalCc + totalGtaNota - totalDesconto + totalDefCv
        return (
          <>
            <div style={{ background: '#f0f0f0', color: '#000', fontWeight: 700, fontSize: 13, textAlign: 'center', padding: '5px 10px', marginBottom: 0, border: '1px solid #000' }}>
              TOTAL GERAL
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, tableLayout: 'fixed' }}>
              <colgroup>
                {totalDesconto > 0 ? (
                  <>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                  </>
                )}
              </colgroup>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#f5f5f5' }}>
                    <div style={{ fontWeight: 700, color: '#555', fontSize: 12 }}>Total Animais</div>
                    <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>{totalAnimais}</div>
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#f5f5f5' }}>
                    <div style={{ fontWeight: 700, color: '#555', fontSize: 12 }}>{totalCompraModo === 'liquido' ? 'Total Compra Líquido' : 'Total Compra'}</div>
                    <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>R$ {num(totalCompraModo === 'liquido' ? totalGeral : totalVv)}</div>
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#eef3f8' }}>
                    <div style={{ fontWeight: 700, color: '#1a3c6b', fontSize: 12 }}>Com./Taxa Geral</div>
                    <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2, color: '#1a3c6b' }}>R$ {num((isSeparada ? totalCc + totalGtaNota : totalCv + totalCc + totalDesp + totalGtaNota) + totalDefCv)}</div>
                  </td>
                  {totalDesconto > 0 && (
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#fff3cd' }}>
                      <div style={{ fontWeight: 700, color: '#92400e', fontSize: 12 }}>Desconto</div>
                      <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2, color: '#92400e' }}>- R$ {num(totalDesconto)}</div>
                    </td>
                  )}
                  <td style={{ border: '1px solid #1a3c6b', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#1a3c6b' }}>
                    <div style={{ fontWeight: 700, color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{isCavalo ? '1ª Parcela + Com. Compra' : 'Gado + Com. + Taxas'}</div>
                    <div style={{ fontWeight: 700, fontSize: 18, marginTop: 2, color: '#fff' }}>R$ {num(totalAPagar)}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )
      })()}

      {/* ── Assinatura ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, fontSize: 13 }}>
        <div>
          <div>{[organizador.cidade, organizador.estado].filter(Boolean).join(' - ')}, {fmtDate(leilao.data_leilao)}</div>
          <div style={{ color: '#1a3c6b', marginTop: 4 }}>Declaro que estou de acordo com as especificações acima.</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: 220, margin: '0 auto 4px' }} />
          <div style={{ fontWeight: 700 }}>{comp.nome}</div>
          <div style={{ color: '#999', fontSize: 10 }}>Assinatura do Comprador</div>
        </div>
      </div>
    </div>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function FaturaCompradorPreview({
  dados,
  orgId,
  leilaoId,
  compradorId,
  tipo,
  showDbVendedor = false,
  showMediasSexo = false,
  showMediaGeral = false,
  showGtaNota    = false,
  ocultarLinhaGtaNota = false,
  ocultarColunaGado   = false,
  comissaoEmLinha      = false,
  ocultarParcelas      = false,
  colunasAtivas        = null,
  ordenarPgtosPorData  = false,
  totalCompraModo      = 'bruto',
  separacao            = null,
  onSeparar,
  onReload,
}) {
  const iframeRef  = useRef(null)
  const [pdfUrl,       setPdfUrl]       = useState(null)
  const [loadingPdf,   setLoadingPdf]   = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [showAlterarComissao, setShowAlterarComissao] = useState(false)
  const [showGtaModal, setShowGtaModal] = useState(false)
  const [showSeparar, setShowSeparar] = useState(false)

  if (!dados) return null
  const { leilao, organizador, compradores } = dados
  // Um único comprador selecionado (mesmo que a fatura esteja separada em várias)
  const unico = compradores.length === 1 || (!!dados.separacao_opcoes && compradores.length > 0)
  // Lotes originais do comprador = união das faturas separadas
  const lotesAll = compradores.flatMap(c => c.lotes || [])

  const fluxo = leilao?.fluxo_comissao || ''
  const mostraBotaoGta = fluxo === 'gta_nota' && unico && compradorId
  const compGta  = unico ? !!compradores[0]?.gta_isento  : false
  const compNota = unico ? !!compradores[0]?.nota_isento : false

  // GTA e Nota ainda cobrados do comprador (para exibir no modal)
  const { gtaNoModal, notaNoModal } = (() => {
    if (!mostraBotaoGta || !compradores[0]) return { gtaNoModal: 0, notaNoModal: 0 }
    const comp = compradores[0]
    const venda = lotesAll.filter(l => l.situacao === 'VENDA')
    const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
    const valorNota = parseFloat(leilao.valor_nota) || 0
    const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
    const lotesGta = venda.filter(l => !l.gta_isento)
    const lotesNota = venda.filter(l => !l.nota_isento)
    const gta = valorPorAnimal * lotesGta.reduce((s, l) => s + (l.qtd || 0), 0)
    const nota = gtaModo === 'por_lote'
      ? valorNota * lotesNota.length
      : valorNota * new Set(lotesNota.filter(l => l.vendedor_id).map(l => l.vendedor_id)).size
    return { gtaNoModal: gta, notaNoModal: nota }
  })()

  const handleImprimir = async (comResumo = false) => {
    setLoadingPdf(true)
    try {
      const qs = new URLSearchParams()
      if (compradorId) qs.set('comprador_id', compradorId)
      qs.set('tipo', tipo || 'padrao')
      if (showDbVendedor) qs.set('dados_bancarios_vendedor', 'true')
      if (!showMediasSexo) qs.set('mostrar_medias_sexo', 'false')
      if (!showMediaGeral) qs.set('mostrar_media_geral', 'false')
      if (!showGtaNota) qs.set('mostrar_gta_nota', 'false')
      if (ocultarLinhaGtaNota) qs.set('ocultar_linha_gta_nota', 'true')
      if (ocultarColunaGado)   qs.set('ocultar_coluna_gado', 'true')
      if (comResumo) qs.set('com_resumo', 'true')
      if (compradorId && separacao) qs.set('separacao', JSON.stringify(separacao))
      const resp = await api.get(
        `/organizadores/${orgId}/mapas/leilao/${leilaoId}/fatura-comprador?${qs}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      openPdf(url, setPdfUrl, setShowPdfModal)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => {
    setShowPdfModal(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  const nomeArquivo = (() => {
    const nomeComp = unico ? compradores[0].nome : `Todos (${compradores.length})`
    return `Fatura do Comprador - ${nomeComp}.pdf`.replace(/[/\\?%*:|"<>]/g, '-')
  })()

  return (
    <>
      {/* ── Barra de ação ── */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-600 flex-1 min-w-0 truncate">
          <span className="font-medium text-gray-800">{leilao.nome}</span>
          {' · '}
          {unico ? compradores[0].nome : `${compradores.length} compradores`}
          {separacao && compradores.length > 1 && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium align-middle">
              Separada em {compradores.length} faturas
              <button onClick={() => onSeparar?.(null)} title="Voltar à fatura única" className="hover:text-red-600"><XIcon className="w-3 h-3" /></button>
            </span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleImprimir()}
            disabled={loadingPdf}
            className="flex items-center gap-1.5 px-4 py-2.5 sm:px-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            <Printer className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{loadingPdf ? 'Gerando...' : 'Imprimir / PDF'}</span>
            <span className="sm:hidden">{loadingPdf ? 'Gerando...' : 'PDF'}</span>
          </button>
          <button
            onClick={() => handleImprimir(true)}
            disabled={loadingPdf}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            <Printer className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{loadingPdf ? 'Gerando...' : 'Fatura + Resumo'}</span>
            <span className="sm:hidden">+Resumo</span>
          </button>
          {dados.separacao_opcoes && onSeparar && (
            <button onClick={() => setShowSeparar(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors"
              title="Separar a fatura por tipo de animal, sexo, raça ou vendedor">
              <Split className="w-3.5 h-3.5" />
              Separar fatura
            </button>
          )}
          {unico && compradorId && (
            <PrazoComissao orgId={orgId} leilaoId={leilaoId} fluxo={fluxo} comprador={compradores[0]} onChanged={onReload} />
          )}
          {unico && (
            <>
              <button onClick={async () => {
                try {
                  const resp = await api.get(
                    `/organizadores/${orgId}/recibos/mapa-comprador/${leilaoId}/${compradores[0].id}/recibo-comissao`,
                    { responseType: 'blob', timeout: 30000 }
                  )
                  const blob = new Blob([resp.data], { type: 'application/pdf' })
                  if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                  openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
                } catch (err) {
                  import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de comissão'))
                }
              }}
                className="flex items-center gap-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Recibo de recebimento de comissões + despesas">
                Recibo Com.
              </button>
              <button onClick={async () => {
                try {
                  const resp = await api.get(
                    `/organizadores/${orgId}/recibos/mapa-comprador/${leilaoId}/${compradores[0].id}/recibo-repasse-todos`,
                    { responseType: 'blob', timeout: 60000 }
                  )
                  const blob = new Blob([resp.data], { type: 'application/pdf' })
                  if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                  openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
                } catch (err) {
                  import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de repasse'))
                }
              }}
                className="flex items-center gap-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Recibo de recebimento do valor a repassar para todos os vendedores">
                Recibo Repasse
              </button>
              <button
                onClick={() => setShowAlterarComissao(true)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Alterar Com.
              </button>
              {mostraBotaoGta && (() => {
                const algum = compGta || compNota
                const txt = compGta && compNota ? 'GTA/Nota isentos'
                  : compGta ? 'GTA isento'
                  : compNota ? 'Nota isenta'
                  : 'Isentar GTA/Nota'
                return (
                  <button
                    onClick={() => setShowGtaModal(true)}
                    className={`flex items-center gap-1 px-3 py-2 text-white text-xs font-medium rounded-lg transition-colors ${algum ? 'bg-blue-500 hover:bg-blue-600' : 'bg-teal-600 hover:bg-teal-700'}`}
                    title="Isentar/reverter GTA e Nota do comprador"
                  >
                    {txt}
                  </button>
                )
              })()}
            </>
          )}
        </div>
      </div>

      {/* ── Aviso mobile (oculto em sm+) ── */}
      <div className="sm:hidden bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>O preview é otimizado para desktop.</p>
        <p className="mt-1">Use os botões acima para gerar o PDF.</p>
      </div>

      {/* ── Preview (oculto no mobile) ── */}
      <div className="hidden sm:block" style={{ background: '#f3f4f6', padding: '24px 0' }}>
        {compradores.map((comp, i) => (
          <div
            key={`${comp.id || 'c'}-${i}`}
            style={{
              maxWidth: 1160,
              margin: '0 auto 24px',
              background: '#fff',
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <FaturaPage comp={comp} leilao={leilao} organizador={organizador} showDbVendedor={showDbVendedor} showMediasSexo={showMediasSexo} showMediaGeral={showMediaGeral} showGtaNota={showGtaNota} ocultarLinhaGtaNota={ocultarLinhaGtaNota} ocultarColunaGado={ocultarColunaGado} comissaoEmLinha={comissaoEmLinha} ocultarParcelas={ocultarParcelas} colunasAtivas={colunasAtivas} ordenarPgtosPorData={ordenarPgtosPorData} totalCompraModo={totalCompraModo} />
          </div>
        ))}
      </div>

      {/* ── Modal PDF ── */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm flex-1 min-w-0 truncate">Fatura do Comprador — {leilao.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => downloadPdf(pdfUrl, nomeArquivo)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span className="hidden sm:inline">Salvar </span>PDF
                </button>
                <button
                  onClick={printPdf}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Imprimir
                </button>
                <button
                  onClick={closePdfModal}
                  className="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* iframe */}
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe
                ref={iframeRef}
                src={pdfUrl}
                title={nomeArquivo}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Isentar/Reverter GTA */}
      {showGtaModal && unico && (
        <IsentarGTAModal
          compradorNome={compradores[0].nome}
          compradorId={compradorId}
          leilaoId={leilaoId}
          orgId={orgId}
          gtaIsento={compGta}
          notaIsento={compNota}
          totalGta={gtaNoModal}
          totalNota={notaNoModal}
          totalGtaNota={gtaNoModal + notaNoModal}
          onClose={() => setShowGtaModal(false)}
          onSaved={() => { setShowGtaModal(false); if (onReload) onReload() }}
        />
      )}

      {/* Modal Separar Fatura */}
      {showSeparar && dados.separacao_opcoes && (
        <SepararFaturaModal
          opcoes={dados.separacao_opcoes}
          fluxo={leilao?.fluxo_comissao}
          atual={separacao}
          criterios={CRITERIOS_COMPRADOR}
          entidade="comprador"
          semDespesa
          onClose={() => setShowSeparar(false)}
          onApply={(cfg) => { setShowSeparar(false); onSeparar?.(cfg) }}
        />
      )}

      {/* Modal Alterar Comissão */}
      {showAlterarComissao && unico && (
        <AlterarComissaoModal
          tipo="compra"
          lotes={lotesAll.filter(l => l.situacao !== 'DEFESA')}
          organizadorId={orgId}
          onClose={() => setShowAlterarComissao(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}
    </>
  )
}
